import { Component, Inject, OnInit } from '@angular/core';
import { IAircraftModel, IJobTitle, IPilotOverrideData } from '../../pay-scales/pay-scales.types';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import * as moment from 'moment';
import { HttpErrorResponse } from '@angular/common/http';
import { PayScalesService } from '../../pay-scales/pay-scales.service';
import { PilotsService } from '../../../shared/services/pilots.service';
import { BaseComponent } from '../../base.component';
import { AlertService } from '../../../core';
import { EmploymentStatusEnum, EmploymentStatusTextEnum } from '../../override-employees/EmploymentStatusEnum';
import { emailDomainValidator } from '../../../shared/validators/email-domain.validator';

@Component({
	selector: 'app-override-employee-dialog',
	templateUrl: './override-employee-dialog.component.html',
	styleUrls: ['./override-employee-dialog.component.scss']
})
export class OverrideEmployeeDialogComponent extends BaseComponent implements OnInit {
	constructor(
		protected alertService: AlertService,
		public dialogRef: MatDialogRef<OverrideEmployeeDialogComponent>,
		@Inject(MAT_DIALOG_DATA) public modalData: IPilotOverrideData,
		private fb: FormBuilder,
		private payScalesService: PayScalesService,
		private pilotService: PilotsService
	) {
		super(alertService);
	}

	get employmentStatuses(): any {
		return EmploymentStatusEnum;
	}

	get employmentStatusTextEnum(): any {
		return EmploymentStatusTextEnum;
	}

	public form: FormGroup;
	public dates: string[] = [];
	public disabled: boolean = true;
	public aircraftModels: IAircraftModel[] = [];
	public jobTitles: IJobTitle[] = [];
	public errorMessage: string;
	public currentDate: string;
	public receivesPhoneStipend: boolean;
	public employmentStatusOptions: string[] = [];

	ngOnInit(): void {
		this.employmentStatusOptions = Object.keys(this.employmentStatusTextEnum);
		this.getJobTitles();
		this.getAircraftModel();

		this.form = this.fb.group({
			jobTitleName: new FormControl({ value: this.modalData.employee.jobTitleName, disabled: this.disabled }),
			aircraftModelName: new FormControl({
				value: this.modalData.employee.aircraftModelName,
				disabled: this.disabled
			}),
			email: new FormControl(this.modalData.employee.emailAddress, [Validators.required, emailDomainValidator()]),
			overrideJobTitleId: [
				this.modalData.employee.scheduledChanges
					? this.modalData.employee.scheduledChanges.overrideJobTitleId
					: this.modalData.employee.overrideJobTitleId
					? this.modalData.employee.overrideJobTitleId
					: null
			],
			overrideAircraftModelId: [
				this.modalData.employee.scheduledChanges
					? this.modalData.employee.scheduledChanges.overrideAircraftModelId
					: this.modalData.employee.overrideAircraftModelId
					? this.modalData.employee.overrideAircraftModelId
					: null
			],
			receivesPhoneStipend: [this.modalData.employee.receivesPhoneStipend],
			employmentStatus: [this.modalData.employee.employmentStatus],
			showInLists: [this.modalData.employee.showInLists],
			// If no schedule changes or no overrides use current date
			effectiveStart: this.modalData.employee.scheduledChanges
				? this.modalData.employee.scheduledChanges.effectiveStart
				: this.modalData.employee.overrideJobTitleId
				? this.modalData.employee.effectiveStart
				: this.modalData.employee.overrideAircraftModelId
				? this.modalData.employee.effectiveStart
				: new FormControl(moment.utc())
		});

		this.form.valueChanges.subscribe((form: any) => {
			if (
				form.receivesPhoneStipend ||
				form.overrideJobTitleId ||
				form.overrideAircraftModelId ||
				form.employmentStatus >= this.employmentStatuses.Active
			) {
				this.form.setErrors(null);
			} else {
				this.form.setErrors({ invalid: true });
			}
		});

		const minDateValue: moment.Moment = moment
			.utc()
			.startOf('month')
			.subtract(1, 'M');
		const maxDateValue: moment.Moment = moment
			.utc()
			.startOf('month')
			.add(12, 'M');
		this.getDates(minDateValue, maxDateValue);
	}

	public getDates(startDate: moment.Moment, endDate: moment.Moment): string[] {
		const now: moment.Moment = startDate.clone();

		while (now.isSameOrBefore(endDate)) {
			this.dates.push(now.format('YYYY-MM-DDTHH:mm:ss'));
			now.add(1, 'month');
		}

		const currentDate: string = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString();
		this.currentDate = this.dates.find(d => new Date(d).toISOString() === currentDate);
		this.form.get('effectiveStart').setValue(this.currentDate);
		return this.dates;
	}

	public getAircraftModel(): void {
		this.payScalesService.getAircraftModel().subscribe(
			(DATA: IAircraftModel[]) => {
				if (DATA) {
					this.aircraftModels = DATA;
				}
			},
			(error: HttpErrorResponse) => {
				console.error(error);
			}
		);
	}

	public getJobTitles(): void {
		this.payScalesService.getJobTitles().subscribe(
			(DATA: IJobTitle[]) => {
				if (DATA) {
					this.jobTitles = DATA;
				}
			},
			(error: HttpErrorResponse) => {
				console.error(error);
			}
		);
	}

	public removeOverrides(): void {
		const date: string = moment.utc(this.form.value.effectiveStart).format('YYYY-MM-DDTHH:mm:ss');
		this.pilotService.deleteOverridePilotData(this.modalData.employee.employeeId, date).subscribe(
			() => {
				this.dialogRef.close(true);
			},
			(error: HttpErrorResponse) => {
				this.errorMessage = error.error;
				this.handleError(error);
			}
		);
	}

	public chosenMonthHandler(normalizedMonth: moment.Moment): void {
		this.form.get('effectiveStart').patchValue(normalizedMonth);
	}

	public close(): void {
		this.dialogRef.close();
	}

	public overrideEmployee(): void {
		this.pilotService.overridePilotData(this.modalData.employee.employeeId, this.form.value).subscribe(
			() => {
				this.dialogRef.close(true);
			},
			(error: HttpErrorResponse) => {
				this.errorMessage = error.error;
			}
		);
	}
}
